import { useMemo } from 'react';
import { z } from 'zod';

import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { isAirAdmin } from '@airreading/common';
import {
  CoachOnboardingRow,
  CoachStatus,
  coachOnboardingIncompleteStepSchema,
  CoachOnboardingIncompleteStepLabelMap,
  getCoachesSortableColumnSchema,
} from '@airreading/common/data';

import { EmptyArray, getCoaches } from '@/lib/api-calls.js';
import { validateRouteRole } from '@/lib/routing.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import { airColumnHelper } from '@/lib/tables.js';

import { EntityTable } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import { Tooltip } from '@/components/tooltip.js';
import { InfoPage } from '@/components/info-page.js';

import { coachesSectionTabs } from '../-components/tabs.js';
import { OnboardingStepCheckbox } from './-components/onboarding-step-checkbox.js';

const onboardingSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  incompleteOnboardingSteps: z.array(coachOnboardingIncompleteStepSchema).optional(),
});

export const Route = createFileRoute('/_layout/teachers/onboarding/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: TeachersOnboardingIndex,
  validateSearch: onboardingSearchSchema,
});

const columnHelper = airColumnHelper<CoachOnboardingRow>(getCoachesSortableColumnSchema);

const coachOnboardingIncompleteStepOptions = coachOnboardingIncompleteStepSchema.options.map((value) => ({
  value,
  label: CoachOnboardingIncompleteStepLabelMap[value],
}));

function MeetingUrlCell({ url }: { url: string | null }) {
  if (!url?.trim()) {
    return '-';
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary underline">
      {url}
    </a>
  );
}

const onboardingTableColumns = [
  columnHelper.accessor('fullNameOrEmail', {
    id: 'fullNameOrEmail',
    header: 'Teacher name',
    cell: ({ row, getValue }) => (
      <Link to="/teachers/$teacherId" params={{ teacherId: row.original.id }}>
        {getValue()}
      </Link>
    ),
  }),

  columnHelper.accessor('email', {
    id: 'email',
    header: 'Email',
  }),

  columnHelper.accessor('zoomPersonalMeetingUrl', {
    id: 'zoomPersonalMeetingUrl',
    header: 'Zoom link',
    cell: ({ getValue }) => <MeetingUrlCell url={getValue()} />,
  }),

  columnHelper.accessor('JPSGoogleMeetUrl', {
    id: 'JPSGoogleMeetUrl',
    header: 'JPS Google Meet link',
    cell: ({ getValue }) => <MeetingUrlCell url={getValue()} />,
  }),

  columnHelper.accessor('airReadingGoogleMeetUrl', {
    id: 'airReadingGoogleMeetUrl',
    header: 'Air Google Meet link',
    cell: ({ getValue }) => <MeetingUrlCell url={getValue()} />,
  }),

  columnHelper.accessor((row) => row, {
    id: 'basicInfoCompleted',
    header: 'Basic info completed',
    cell: ({ getValue }) => (
      <Tooltip trigger={<Checkbox checked={getValue().basicInfoCompleted} disabled />}>System operation</Tooltip>
    ),
  }),

  columnHelper.accessor((row) => row, {
    id: 'documentsReviewPassed',
    header: 'Documents review passed',
    cell: ({ getValue }) => <OnboardingStepCheckbox coach={getValue()} field="documentsReviewPassed" />,
  }),

  columnHelper.accessor((row) => row, {
    id: 'workshopPassed',
    header: 'Workshop passed',
    cell: ({ getValue }) => <OnboardingStepCheckbox coach={getValue()} field="workshopPassed" />,
  }),

  columnHelper.accessor((row) => row, {
    id: 'onboardingTechCheckPassed',
    header: 'Tech check passed',
    cell: ({ getValue }) => <OnboardingStepCheckbox coach={getValue()} field="onboardingTechCheckPassed" />,
  }),
];

function TeachersOnboardingIndex() {
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[nameQuery, activeNameQuery], setNameQuery],
      incompleteOnboardingSteps: [[incompleteStepsFilter, activeIncompleteStepsFilter], setIncompleteStepsFilter],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'incompleteOnboardingSteps']);

  const { data, isPending, error, refetch } = useQuery(
    getCoaches({
      pageIndex,
      pageSize,
      filters: {
        query: activeNameQuery,
        statuses: [CoachStatus.onboarding],
        incompleteOnboardingSteps: activeIncompleteStepsFilter,
      },
    }),
  );

  const coaches = useMemo(() => data?.coaches || EmptyArray, [data]);
  useErrorToast(error, 'Error loading onboarding teachers');

  const table = useReactTable({
    data: coaches,
    columns: onboardingTableColumns,
    state: {
      pagination: { pageIndex, pageSize },
    },
    getRowId: (row) => row.id,
    rowCount: data?.total || 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
  });

  const filterComponents = (
    <>
      <TableFilterInputField
        placeholder="Search name or email"
        value={nameQuery ?? ''}
        onChange={(e) => setNameQuery(e.target.value)}
      />
      <TableFilterDropdown
        title="Incomplete steps"
        options={coachOnboardingIncompleteStepOptions}
        selectedValues={incompleteStepsFilter ?? []}
        onChange={setIncompleteStepsFilter}
      />
      <TableFilterResetButton show onClick={resetFilters} />
    </>
  );

  return (
    <InfoPage headerText="Teachers" tabs={coachesSectionTabs()}>
      <EntityTable
        table={table}
        showPagination
        isLoading={isPending}
        hasError={!!error}
        emptyState="No teachers in onboarding"
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents: filterComponents }}
      />
    </InfoPage>
  );
}
